import { v4 as uuidv4 } from 'uuid';

/**
 * Resolve the order-analysis id of an order, keeping it STABLE for the order's whole life.
 *
 * The photo is uploaded carrying the id stored in IndexedDB, and /item-comparison later looks
 * the photos up by that same id. So a second id is not a cosmetic duplicate: the comparison
 * finds 0 photos and stores an analysis with empty detections (EMPTY_MODEL_DETECTIONS), which
 * — being the most recent row — becomes the verdict shown to the restaurant.
 *
 * The server row does not exist until notify-photo creates it, so any refetch or realtime event
 * arriving before the first photo carries no `orderAnalyses`. Minting a fresh uuid there (and
 * persisting it) is what breaks the link. Reuse the persisted id instead; mint only when neither
 * side has one.
 *
 * @param {Array|Object|null} serverAnalyses - `order.orderAnalyses` as GraphQL returns it (array
 *   from a query, single object once normalised) or nothing at all.
 * @param {Object|null} localOrder - the order as stored in IndexedDB, if any.
 * @param {boolean} proActivated - Pro is off → the order has no analysis at all.
 * @returns {string|null}
 */
export function resolveOrderAnalysisId(serverAnalyses, localOrder, proActivated) {
    const serverId = Array.isArray(serverAnalyses)
        ? serverAnalyses[0]?.id
        : serverAnalyses?.id;
    if (serverId) return serverId;
    if (localOrder?.orderAnalysis) return localOrder.orderAnalysis;
    return proActivated ? uuidv4() : null;
}

export async function retry(fn, maxAttempts, delay) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            return await fn();
        } catch (error) {
            if (attempt === maxAttempts) throw error;
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

/**
 * Races a promise against a timeout so a hanging network call (Cognito token
 * refresh, AppSync query) can never block the app indefinitely. The underlying
 * promise is not cancellable, so on timeout we simply stop awaiting it and reject
 * with a recognizable Error — the caller is then free to retry or surface an error.
 *
 * @param {Promise} promise - The promise to guard.
 * @param {number} ms - Timeout in milliseconds.
 * @param {string} label - Human-readable label used in the timeout error message.
 * @returns {Promise} Resolves/rejects with the original promise, or rejects on timeout.
 */
export function withTimeout(promise, ms, label = 'operation') {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
            const err = new Error(`${label} timed out after ${ms}ms`);
            err.name = 'TimeoutError';
            reject(err);
        }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Sanitizes a single S3 object-key segment so the resulting key only contains
 * characters that travel safely through S3, presigned URLs and the downstream
 * analysis pipeline.
 *
 * S3 itself tolerates almost any UTF-8 byte in a key, but segments with spaces,
 * accented letters, emojis or symbols (#, %, ?, &, ...) regularly break presigned
 * URLs and tooling -- which is why some photos never make it to the bucket. The
 * device id comes from the Cognito username and the restaurant id from the
 * backend, so neither is guaranteed to be S3-safe.
 *
 * Strategy: strip diacritics ("Cafe" with accent -> "Cafe"), replace any remaining
 * character outside [A-Za-z0-9._-] with an underscore, collapse repeated
 * underscores and trim dangling separators. Must be applied per-segment: the '/'
 * path delimiter is intentionally not allowed and is dropped here.
 *
 * A segment made entirely of unsafe characters would sanitize down to an empty
 * string, producing a key with an empty component ("//uuid.jpg") \u2014 also invalid.
 * Fall back to a placeholder so the upload still lands and the bad id is visible
 * in the bucket instead of silently failing.
 *
 * @param {string} segment - A single object-key component (without '/').
 * @param {string} fallback - Value used when sanitization leaves nothing.
 * @returns {string} The sanitized, S3-safe segment.
 */
export function sanitizeS3PathSegment(segment, fallback = 'unknown') {
    const sanitized = String(segment ?? '')
        .normalize('NFKD')                  // separate accented letters into base char + diacritic
        .replace(/[\u0300-\u036f]/g, '')            // strip the combining diacritical marks left by NFKD (accents -> base letter)
        .replace(/[^A-Za-z0-9._-]/g, '_')   // anything not S3-safe becomes an underscore
        .replace(/_+/g, '_')                // collapse runs of underscores
        .replace(/^[._-]+|[._-]+$/g, '');   // trim leading/trailing separators
    return sanitized || fallback;
}

/**
 * Sanitizes a single S3 user-metadata VALUE.
 *
 * Object metadata travels as `x-amz-meta-*` HTTP headers, which are signed by
 * SigV4 and must be US-ASCII: a non-ASCII byte (accented restaurant name, emoji
 * in a POS-supplied order id) makes the browser reject the header outright
 * ("Failed to execute 'fetch': Invalid value") or produces a signature mismatch,
 * so the photo is retried three times and then never reaches the bucket. Control
 * characters are worse \u2014 CR/LF would allow header injection.
 *
 * Downstream consumers also treat these values as identifiers (the trigger lambda
 * derives a Step Functions execution name from them, and SFN forbids characters
 * like ':' '/' '?' '*'), so the safe set is deliberately narrow rather than "any
 * printable ASCII": strip diacritics, keep [A-Za-z0-9._-] plus space, replace the
 * rest with '_'. Values are capped because S3 limits the total user-metadata size
 * to 2 KB.
 *
 * @param {*} value - The raw metadata value.
 * @param {number} maxLength - Maximum length of the returned value.
 * @returns {string} The sanitized, header-safe value.
 */
export function sanitizeS3MetadataValue(value, maxLength = 256) {
    return String(value ?? '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9._ -]/g, '_')
        .replace(/_+/g, '_')
        .trim()
        .slice(0, maxLength);
}

/**
 * Sanitizes every value of an S3 metadata object with {@link sanitizeS3MetadataValue},
 * dropping entries that are null/undefined or that sanitize to an empty string \u2014
 * an empty `x-amz-meta-*` header carries no information and only adds a way for the
 * request to be rejected. Keys are lowercased and sanitized too: S3 lowercases
 * metadata keys anyway, and a key with an unexpected character is an invalid header name.
 *
 * @param {Object} metadata - Raw metadata map.
 * @returns {Object} A new map with sanitized keys and values.
 */
export function sanitizeS3Metadata(metadata) {
    const safe = {};
    for (const [key, value] of Object.entries(metadata || {})) {
        if (value === null || value === undefined) continue;
        const safeKey = String(key).toLowerCase().replace(/[^a-z0-9-]/g, '-');
        const safeValue = sanitizeS3MetadataValue(value);
        if (!safeKey || !safeValue) continue;
        safe[safeKey] = safeValue;
    }
    return safe;
}

export function getOrderFromIndexedDB(dbInstance, key) {
    return new Promise((resolve, reject) => {
      if (!dbInstance) {
        reject(new Error('Database instance is not available'));
        return;
      }
  
      let transaction, store;
      try {
        transaction = dbInstance.transaction(['orders'], 'readonly');
        store = transaction.objectStore('orders');
      } catch (error) {
        reject(new Error(`Failed to start transaction: ${error.message}`));
        return;
      }
  
      let request;
      try {
        request = store.get(key);
      } catch (error) {
        reject(new Error(`Failed to initiate get request: ${error.message}`));
        return;
      }
  
      request.onsuccess = (event) => {
        const result = event.target.result;
        if (result === undefined) {
          resolve(null); // Order not found, but this is not an error
        } else {
          resolve(result);
        }
      };
  
      request.onerror = (event) => {
        reject(new Error(`IndexedDB get request failed: ${event.target.error}`));
      };
    });
  }
  
  export function saveOrderToIndexedDB(dbInstance, order) {
    return new Promise((resolve, reject) => {
      if (!dbInstance) {
        reject(new Error('Database instance is not available'));
        return;
      }

      const transaction = dbInstance.transaction(['orders'], 'readwrite');
      const store = transaction.objectStore('orders');

      const request = store.put(order);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        reject(new Error(`Failed to save order: ${event.target.error}`));
      };

      transaction.onabort = (event) => {
        reject(new Error(`Transaction aborted while saving order: ${event.target.error}`));
      };
    });
  }

  export async function getAllPendingPhotos(dbInstance) {
    // Every photo still in the local queue (they are deleted once uploaded), across all
    // orders — used to seed the pending-photo counters after a reload.
    return new Promise((resolve, reject) => {
      if (!dbInstance) {
        reject(new Error('Database instance is not available'));
        return;
      }
      const transaction = dbInstance.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const request = store.getAll();

      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }

  export async function getPhotosByOrderId(dbInstance, orderId) {
    return new Promise((resolve, reject) => {
      if (!dbInstance) {
        reject(new Error('Database instance is not available'));
        return;
      }
      const transaction = dbInstance.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const index = store.index('order');
      const request = index.getAll(orderId);

      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }

  export async function updateAnalysisInDB(dbInstance, orderKey, analysisResult){
    const writeTransaction = dbInstance.transaction('orders', 'readwrite');
    const writeStore = writeTransaction.objectStore('orders');
    const updateRequest = writeStore.get(orderKey);
  
    return new Promise((resolve, reject) => {
      updateRequest.onsuccess = (event) => {
        const updatedOrderData = event.target.result;
        if (!updatedOrderData) {
          reject(new Error(`Order not found in DB for key: ${orderKey}`));
          return;
        }
        updatedOrderData.analysisResult = JSON.stringify(analysisResult);
        writeStore.put(updatedOrderData);
        resolve();
      };
      updateRequest.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
  
  export function openDatabase(dbName, version) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, version);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const oldVersion = event.oldVersion;

        // On a version bump, reset the transient stores (photos, orders) but NEVER delete 'settings':
        // it holds the device's camera configuration (the remote-config cache). Wiping it on a DB
        // version bump would make every tablet lose its config, and the first-run migration would
        // then push DEFAULTS up to the backend. So 'settings' is always preserved across upgrades.
        if (oldVersion < version && oldVersion > 0) {
          for (const storeName of ['photos', 'orders']) {
            if (db.objectStoreNames.contains(storeName)) {
              db.deleteObjectStore(storeName);
            }
          }
        }

        // Create any store that doesn't exist yet (idempotent — covers fresh DBs and post-reset).
        if (!db.objectStoreNames.contains('photos')) {
          const store = db.createObjectStore('photos', {keyPath: 'key'});
          store.createIndex('order', 'order', {unique: false});
        }
        if (!db.objectStoreNames.contains('orders')) {
          db.createObjectStore('orders', {keyPath: 'key'});
        }
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', {keyPath: 'key'});
        }
      };
      
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      
      request.onerror = (event) => {
        reject(new Error(`Failed to open database: ${event.target.error}`));
      };
    });
  }
  